import { Transaction, TransactionParameters } from "../types";

/**
 * List of common transaction purposes
 */
const TRANSACTION_PURPOSES = [
  "Invoice payment",
  "Monthly fee",
  "Subscription",
  "Refund",
  "Service payment",
  "Salary",
  "Bonus",
  "Rent",
  "Insurance",
  "Utilities",
  "Tax payment",
  "Dividend",
  "Interest payment",
];

/**
 * Format date to YYYY-MM-DD
 */
const formatDate = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

/**
 * Generate a random IBAN with the given prefix
 */
const generateRandomIban = (ibanPrefix: string): string => {
  const countryCode = ibanPrefix.substring(0, 2) || "DE";
  const digits = "0123456789";
  let iban = countryCode;

  // Add check digits and bank code
  iban += "00";
  for (let i = 0; i < 8; i++) {
    iban += digits.charAt(Math.floor(Math.random() * digits.length));
  }

  // Add account number
  for (let i = 0; i < 10; i++) {
    iban += digits.charAt(Math.floor(Math.random() * digits.length));
  }

  // Add spaces for readability
  return iban.replace(/(.{4})/g, "$1 ").trim();
};

/**
 * Calculate a random amount based on parameters
 */
const calculateAmount = (
  min: number,
  max: number,
  transactionType: string
): number => {
  // Calculate random amount with 2 decimal places
  let amount = parseFloat((Math.random() * (max - min) + min).toFixed(2));

  // Apply credit/debit sign
  if (transactionType === "debit") {
    return -Math.abs(amount);
  } else if (transactionType === "credit") {
    return Math.abs(amount);
  } else {
    // For 'both' type, randomly decide credit or debit
    return Math.random() > 0.5 ? amount : -amount;
  }
};

/**
 * Generate a random date between start and end dates
 */
const generateRandomDate = (startDate: Date, endDate: Date): Date => {
  return new Date(
    startDate.getTime() +
      Math.random() * (endDate.getTime() - startDate.getTime())
  );
};

/**
 * Generate transaction data based on provided parameters
 */
export const generateTransactionData = (
  params: TransactionParameters
): Transaction[] => {
  const {
    amountMin,
    amountMax,
    transactionType,
    bookingDateStart,
    bookingDateEnd,
    purposePrefix,
    senderNameTemplate,
    ibanPrefix,
    count,
  } = params;

  const newTransactions: Transaction[] = [];
  const startDate = new Date(bookingDateStart);
  const endDate = new Date(bookingDateEnd);

  for (let i = 0; i < count; i++) {
    // Calculate amount with credit/debit sign
    const amount = calculateAmount(amountMin, amountMax, transactionType);

    // Generate random booking date
    const bookingDate = generateRandomDate(startDate, endDate);

    // Generate purpose
    const randomPurpose =
      TRANSACTION_PURPOSES[
        Math.floor(Math.random() * TRANSACTION_PURPOSES.length)
      ];
    const purpose = purposePrefix
      ? `${purposePrefix} ${randomPurpose}`
      : randomPurpose;

    // Generate sender name
    const senderName = `${senderNameTemplate || "Test Sender"} ${i + 1}`;

    // Create transaction object
    newTransactions.push({
      id: Date.now() + i, // Create a unique ID
      amount,
      purpose,
      senderName,
      iban: generateRandomIban(ibanPrefix),
      bookingDate: formatDate(bookingDate),
      valutaDate: formatDate(bookingDate),
    });
  }

  return newTransactions;
};
